import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { ptBR } from 'date-fns/locale';

import clienteCreditoService from '../../services/clienteCreditoService';
import ClientSelect from '../selects/ClientSelect';

interface CreditoFormData {
  cliente_id: number | null;
  tipo: 'cadastro' | 'recorrente' | 'bonificacao';
  valor: number;
  saldo: number;
  cliente_origem_id?: number;
  nivel?: number;
  observacoes?: string;
}

interface CreditoFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const CreditoForm: React.FC<CreditoFormProps> = ({
  open,
  onClose,
  onSuccess,
  onError
}) => {
  const [formData, setFormData] = useState<CreditoFormData>({
    cliente_id: null,
    tipo: 'bonificacao',
    valor: 0,
    saldo: 0,
    cliente_origem_id: undefined,
    nivel: undefined,
    observacoes: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Estados para valores formatados em moeda
  const [valorFormatado, setValorFormatado] = useState('0,00');
  const [saldoFormatado, setSaldoFormatado] = useState('0,00');

  // Função para formatar valor para moeda brasileira
  const formatarMoeda = (valor: number): string => {
    return valor.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para converter string formatada para número
  const parsearMoeda = (valorFormatado: string): number => {
    // Remove todos os caracteres que não são números, vírgula ou ponto
    const numeroLimpo = valorFormatado.replace(/[^\d,]/g, '');
    // Substitui vírgula por ponto para conversão
    const numeroComPonto = numeroLimpo.replace(',', '.');
    const numero = parseFloat(numeroComPonto) || 0;
    return numero;
  };

  // Função para aplicar máscara de moeda durante a digitação
  const aplicarMascaraMoeda = (valor: string): string => {
    // Remove tudo que não é número
    const apenasNumeros = valor.replace(/\D/g, '');

    if (apenasNumeros === '') return '0,00';

    // Converte para número e divide por 100 para considerar os centavos
    const numero = parseInt(apenasNumeros) / 100;

    // Formata como moeda brasileira
    return numero.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };


  const handleInputChange = (field: keyof CreditoFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Limpar erro do campo quando alterado
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Atualizar saldo automaticamente quando valor for alterado
    if (field === 'valor') {
      setFormData(prev => ({ ...prev, saldo: value }));
      setSaldoFormatado(formatarMoeda(value));
    }

    // Se o tipo foi alterado para bonificação, limpar campos relacionados
    if (field === 'tipo' && value === 'bonificacao') {
      setFormData(prev => ({
        ...prev,
        cliente_origem_id: undefined,
        nivel: undefined
      }));

      // Limpar erros dos campos ocultos
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.cliente_origem_id;
        delete newErrors.nivel;
        return newErrors;
      });
    }
  };

  // Handlers para campos de moeda
  const handleValorChange = (valor: string) => {
    const valorFormatado = aplicarMascaraMoeda(valor);
    const valorNumerico = parsearMoeda(valorFormatado);

    setValorFormatado(valorFormatado);
    handleInputChange('valor', valorNumerico);
  };

  const handleSaldoChange = (valor: string) => {
    const valorFormatado = aplicarMascaraMoeda(valor);
    const valorNumerico = parsearMoeda(valorFormatado);

    setSaldoFormatado(valorFormatado);
    handleInputChange('saldo', valorNumerico);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.cliente_id) {
      newErrors.cliente_id = 'Cliente é obrigatório';
    }

    if (!formData.tipo) {
      newErrors.tipo = 'Tipo é obrigatório';
    }

    if (!formData.valor || formData.valor <= 0) {
      newErrors.valor = 'Valor deve ser maior que zero';
    }

    if (!formData.saldo || formData.saldo <= 0) {
      newErrors.saldo = 'Saldo deve ser maior que zero';
    }

    // Verificar se cliente origem é diferente do cliente (apenas se foi informado e não for bonificação)
    if (formData.tipo !== 'bonificacao' && formData.cliente_origem_id && formData.cliente_id === formData.cliente_origem_id) {
      newErrors.cliente_origem_id = 'Cliente origem deve ser diferente do cliente';
    }

    // Validar nível apenas se foi informado e não for bonificação
    if (formData.tipo !== 'bonificacao' && formData.nivel && (formData.nivel < 1 || formData.nivel > 5)) {
      newErrors.nivel = 'Nível deve ser entre 1 e 5';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Criar o crédito usando o service
      const creditoData: any = {
        cliente_id: formData.cliente_id,
        tipo: formData.tipo,
        valor: formData.valor,
        saldo: formData.saldo,
        utilizado: false
      };

      // Adicionar campos opcionais apenas se não for bonificação
      if (formData.tipo !== 'bonificacao') {
        if (formData.cliente_origem_id) {
          creditoData.cliente_origem_id = formData.cliente_origem_id;
        }
        if (formData.nivel) {
          creditoData.nivel = formData.nivel;
        }
      }

      if (formData.observacoes) {
        creditoData.observacoes = formData.observacoes;
      }

      await clienteCreditoService.createCredito(creditoData);

      onSuccess();
      handleClose();
    } catch (error: any) {
      onError(error.message || 'Erro ao criar crédito');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      cliente_id: null,
      tipo: 'bonificacao',
      valor: 0,
      saldo: 0,
      cliente_origem_id: undefined,
      nivel: undefined,
      observacoes: ''
    });
    setErrors({});
    setValorFormatado('0,00');
    setSaldoFormatado('0,00');
    onClose();
  };


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Novo Crédito
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Crie um novo crédito para um cliente
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            {/* Cliente */}
            <Grid item xs={12} md={6}>
              <ClientSelect
                value={formData.cliente_id}
                onChange={(clientId) => handleInputChange('cliente_id', clientId)}
                label="Cliente"
                required
                error={!!errors.cliente_id}
                helperText={errors.cliente_id}
                size='small'
              />
            </Grid>

            {/* Tipo */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.tipo} required>
                <InputLabel>Tipo *</InputLabel>
                <Select
                  value={formData.tipo}
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                  label="Tipo *"
                  size='small'
                >
                  <MenuItem value="bonificacao">Bonificação</MenuItem>
                  <MenuItem value="cadastro">Cadastro</MenuItem>
                  <MenuItem value="recorrente">Recorrente</MenuItem>
                </Select>
                {errors.tipo && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.tipo}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Cliente Origem - Oculto para bonificação */}
            {formData.tipo !== 'bonificacao' && (
              <Grid item xs={12} md={6}>
                <ClientSelect
                  value={formData.cliente_origem_id}
                  onChange={(clientId) => handleInputChange('cliente_origem_id', clientId)}
                  label="Cliente Origem"
                  error={!!errors.cliente_origem_id}
                  helperText={errors.cliente_origem_id || 'Cliente que originou o crédito (opcional)'}
                  size='small'
                />
              </Grid>
            )}

            {/* Nível - Oculto para bonificação */}
            {formData.tipo !== 'bonificacao' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Nível"
                  value={formData.nivel || ''}
                  onChange={(e) => handleInputChange('nivel', e.target.value ? parseInt(e.target.value) : undefined)}
                  error={!!errors.nivel}
                  helperText={errors.nivel || 'Nível do crédito (opcional)'}
                  size="small"
                  inputProps={{ min: 1, max: 5 }}
                />
              </Grid>
            )}


            {/* Valor */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Valor *"
                value={valorFormatado}
                onChange={(e) => handleValorChange(e.target.value)}
                error={!!errors.valor}
                helperText={errors.valor || 'Valor original do crédito'}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                size='small'
              />
            </Grid>

            {/* Saldo */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Saldo *"
                value={saldoFormatado}
                onChange={(e) => handleSaldoChange(e.target.value)}
                error={!!errors.saldo}
                helperText={errors.saldo || 'Saldo disponível do crédito'}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                size='small'
              />
            </Grid>

            {/* Observações */}
            <Grid item xs={12}>
              <TextField
                size='small'
                fullWidth
                label="Observações"
                multiline
                rows={3}
                value={formData.observacoes}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
                helperText="Observações adicionais sobre o crédito (opcional)"
              />
            </Grid>
          </Grid>

          {/* Informações do tipo de crédito */}
          <Box sx={{
            mt: 1,
            p: 1,
            bgcolor: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'grey.50',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1
          }}>
            <Typography variant="subtitle2" gutterBottom>
              Informações sobre o tipo de crédito:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Cadastro:</strong> Crédito por indicação de novo cliente
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Recorrente:</strong> Crédito mensal por cliente ativo
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Bonificação:</strong> Crédito especial por promoção ou campanha
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Criando...' : 'Criar Crédito'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreditoForm;
